import { useState } from 'react'
import './AlwaysAllowMenu.css'

type DurationChoice = { label: string; compute: () => string | null }

const PRO_CHOICES: DurationChoice[] = [
  { label: '5 Minutes', compute: () => toISO(5 * 60 * 1000) },
  { label: '1 Hour', compute: () => toISO(60 * 60 * 1000) },
  {
    label: 'Until Tonight',
    compute: () => {
      const d = new Date()
      d.setHours(24, 0, 0, 0) // start of tomorrow
      return d.toISOString()
    },
  },
  { label: '1 Week', compute: () => toISO(7 * 24 * 60 * 60 * 1000) },
  { label: '1 Month', compute: () => toISO(30 * 24 * 60 * 60 * 1000) },
  { label: 'Forever', compute: () => null },
]

function toISO(msFromNow: number): string {
  return new Date(Date.now() + msFromNow).toISOString()
}

/// Button that fires Always-Allow or Always-Deny depending on
/// `decision`. Tap = forever; the secondary "⋯" affordance opens the
/// expiring-grant duration picker (matches SwiftUI's Menu+primaryAction
/// pattern). Expiring grants used to be Pro-gated; entitlement enforcement
/// is disconnected now (everything is free), so the durations are always
/// available. See docs/entitlements-disconnected.md. Deny variant is styled
/// red via a modifier class so the destructive-direction surface reads at a
/// glance.
export function AlwaysAllowMenu({
  onChoose,
  decision = 'allow',
}: {
  onChoose: (expiresAt: string | null) => void
  decision?: 'allow' | 'deny'
}) {
  const [open, setOpen] = useState(false)

  function primary() {
    // Tap = forever; the ⋯ affordance opens the expiring-grant durations.
    onChoose(null)
  }

  const isDeny = decision === 'deny'
  const label = isDeny ? '✗ Always Deny' : '✓ Always Allow'
  const sheetTitle = isDeny ? 'Always deny for' : 'Always allow for'

  return (
    <>
      <div className="aa-menu">
        {/* Outer is a div acting as a button so the affordance inside can
          be a real <button> without tripping HTML's "no nested buttons"
          rule. Click falls through to primary() unless the affordance
          stops propagation. */}
        <div
          className={`aa-menu__primary ${isDeny ? 'aa-menu__primary--deny' : ''}`}
          role="button"
          tabIndex={0}
          onClick={primary}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              primary()
            }
          }}
        >
          <span>{label}</span>
          <button
            className="aa-menu__affordance"
            aria-label="Duration options"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(true)
            }}
          >
            ⋯
          </button>
        </div>
      </div>

      {open && (
        <div
          className="aa-menu__sheet-backdrop"
          onClick={() => setOpen(false)}
        >
          <div
            className="aa-menu__sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aa-menu__sheet-title">{sheetTitle}</div>
            {PRO_CHOICES.map((c) => (
              <button
                key={c.label}
                className="aa-menu__choice"
                onClick={() => {
                  setOpen(false)
                  onChoose(c.compute())
                }}
              >
                {c.label}
              </button>
            ))}
            <button
              className="aa-menu__choice aa-menu__choice--cancel"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  )
}
